import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Archive, CheckCircle2, RotateCcw, Save, Sparkles } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { API, errorText } from '../governance_v2/constants';
import { EVENT_LABELS, SOURCE_LABELS, STATUS_META, VERDICT_META } from './constants';

const Box = ({ title, children }) => (
  <div className="space-y-1"><p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">{title}</p>
    <div className="text-sm text-slate-700 dark:text-slate-200">{children}</div></div>
);

const useEdit = (companyId, id) => {
  const qc = useQueryClient();
  const done = (msg) => { toast.success(msg); qc.invalidateQueries({ queryKey: ['memory'] }); };
  return {
    patch: useMutation({ mutationFn: (body) => axios.patch(`${API}/companies/${companyId}/memory/${id}`, body),
      onSuccess: (_, b) => done(b.status === 'aprobada' ? 'Lección aprobada: ya la usan SUPER-100 y el briefing' : 'Memoria actualizada'),
      onError: (e) => toast.error(errorText(e)) }),
    redraft: useMutation({ mutationFn: () => axios.post(`${API}/companies/${companyId}/memory/${id}/redraft`),
      onSuccess: () => done('Nuevo borrador redactado'), onError: (e) => toast.error(errorText(e)) }),
  };
};

const AdminActions = ({ m, lesson, companyId }) => {
  const { patch, redraft } = useEdit(companyId, m.id);
  const busy = patch.isPending || redraft.isPending;
  return (
    <div className="flex flex-wrap gap-2">
      {lesson !== m.lesson && <Button size="sm" variant="outline" disabled={busy || lesson.trim().length < 10} onClick={() => patch.mutate({ lesson })} data-testid="memory-save-btn"><Save className="mr-1 h-3.5 w-3.5" />Guardar texto</Button>}
      {m.status !== 'aprobada' && <Button size="sm" disabled={busy || lesson.trim().length < 10} onClick={() => patch.mutate({ status: 'aprobada', ...(lesson !== m.lesson && { lesson }) })} data-testid="memory-approve-btn"><CheckCircle2 className="mr-1 h-3.5 w-3.5" />Aprobar</Button>}
      {m.event !== 'manual' && m.status !== 'aprobada' && <Button size="sm" variant="ghost" disabled={busy} onClick={() => redraft.mutate()} data-testid="memory-redraft-btn"><Sparkles className="mr-1 h-3.5 w-3.5" />{redraft.isPending ? 'Redactando…' : 'Redactar de nuevo'}</Button>}
      {m.status !== 'archivada' && <Button size="sm" variant="ghost" disabled={busy} onClick={() => patch.mutate({ status: 'archivada' })} data-testid="memory-archive-btn"><Archive className="mr-1 h-3.5 w-3.5" />Archivar</Button>}
      {m.status === 'archivada' && <Button size="sm" variant="ghost" disabled={busy} onClick={() => patch.mutate({ status: 'borrador' })} data-testid="memory-restore-btn"><RotateCcw className="mr-1 h-3.5 w-3.5" />Restaurar</Button>}
    </div>
  );
};

export const MemoryDetailDialog = ({ companyId, memoryId, isAdmin, onClose }) => {
  const { data: m } = useQuery({ queryKey: ['memory', 'detail', memoryId], enabled: !!memoryId,
    queryFn: async () => (await axios.get(`${API}/companies/${companyId}/memory/${memoryId}`)).data.memory });
  const [lesson, setLesson] = useState('');
  useEffect(() => { if (m) setLesson(m.lesson); }, [m]);
  return (
    <Dialog open={!!memoryId} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[85vh] max-w-2xl overflow-y-auto" data-testid="memory-detail-dialog">
        {m && <>
          <DialogHeader><DialogTitle className="pr-6">{m.title}</DialogTitle></DialogHeader>
          <div className="flex flex-wrap gap-2">
            <span className={`rounded px-2 py-0.5 text-xs font-bold ${VERDICT_META[m.outcome.verdict]?.cls}`}>{VERDICT_META[m.outcome.verdict]?.label}</span>
            <Badge variant="outline" className={STATUS_META[m.status].cls} data-testid="memory-detail-status">{STATUS_META[m.status].label}</Badge>
            <Badge variant="outline">{EVENT_LABELS[m.event]}</Badge>
            {m.outcome_changed && <Badge className="on-solid bg-orange-600 text-white" data-testid="memory-outcome-changed">El resultado cambió desde la aprobación</Badge>}
          </div>
          {m.decision && <Box title="Decisión">{m.decision.title}{m.decision.description && <p className="mt-1 text-xs text-slate-500">{m.decision.description}</p>}</Box>}
          <Box title="Resultado medido">{m.outcome.summary || '—'}</Box>
          <Box title={`Lección · ${SOURCE_LABELS[m.lesson_source] || m.lesson_source}`}>
            {isAdmin ? <Textarea rows={4} value={lesson} onChange={(e) => setLesson(e.target.value)} data-testid="memory-lesson-input" />
              : <p data-testid="memory-lesson-text">{m.lesson}</p>}
          </Box>
          {m.tags?.length > 0 && <div className="flex flex-wrap gap-1">{m.tags.map((t) => <span key={t} className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] text-slate-600 dark:bg-slate-800 dark:text-slate-300">{t}</span>)}</div>}
          {isAdmin && <AdminActions m={m} lesson={lesson} companyId={companyId} />}
          <Box title="Línea de tiempo"><ol className="space-y-1 border-l border-slate-300 pl-3 dark:border-slate-600" data-testid="memory-history">
            {(m.history || []).map((h, i) => <li key={i} className="text-xs text-slate-500">{new Date(h.at).toLocaleString('es')} · {h.event.replace(/_/g, ' ')}{h.by ? ` · ${h.by}` : ''}</li>)}</ol></Box>
        </>}
      </DialogContent>
    </Dialog>
  );
};
